import React from 'react';
import { Box, Button, IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { RowAction } from '../../../../../components/row-action';
import { ICommitmentResponse } from '../../../../../model/sge/commitment/response/commitment-response';
import { formatDate } from '../../../../../utils/date';
import { formatCurrency } from '../../../../../utils/format';
import { AmendmentDetailController } from '../context/amendment-detail-controller';
import { useAmendmentDetailState } from '../context/amendment-detail-context';
import { sumAmounts } from './amendment-detail-form-utils';
import { EmptyRow, NestedPanelHeader, dashIfEmpty } from './detail-parts';
import { InlineProgress } from './progress-stat';
import { PaymentsPanel } from './payments-panel';

const COLUMN_COUNT = 6;

// Liquidações do empenho selecionado; cada liquidação expande para mostrar seus pagamentos
export function LiquidationsPanel({ commitment, ctrl }: { commitment: ICommitmentResponse; ctrl: AmendmentDetailController }) {
    const { liquidations, payments, expandedLiquidationId } = useAmendmentDetailState();

    return (
        <Box sx={{ pl: { xs: 2, sm: 5 }, pr: 2, pb: 2.5, pt: 0.5 }}>
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', borderLeft: '3px solid', borderLeftColor: 'primary.main' }}>
                <NestedPanelHeader
                    icon={<ReceiptLongIcon fontSize="small" color="primary" />}
                    title={`Liquidações (${liquidations.length})`}
                    action={<Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => ctrl.openDrawer('liquidationDrawer')}>Nova Liquidação</Button>}
                    progress={<InlineProgress current={sumAmounts(liquidations, (l) => l.liquidatedAmount)} total={commitment.committedAmount || 0} />}
                />
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell width={40} />
                            <TableCell>Nº Liquidação</TableCell>
                            <TableCell>Data</TableCell>
                            <TableCell align="right">Valor Liquidado</TableCell>
                            <TableCell>Observações</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {liquidations.length === 0 && <EmptyRow colSpan={COLUMN_COUNT} message="Nenhuma liquidação registrada" />}
                        {liquidations.map((liquidation) => {
                            const isExpanded = expandedLiquidationId === liquidation.id;
                            return (
                                <React.Fragment key={liquidation.id}>
                                    <TableRow
                                        hover
                                        selected={isExpanded}
                                        sx={{ cursor: 'pointer', '& td': { borderBottom: isExpanded ? 'none' : undefined } }}
                                        onClick={() => ctrl.handleToggleLiquidation(liquidation)}
                                    >
                                        <TableCell>
                                            <IconButton size="small" aria-label={isExpanded ? 'Recolher pagamentos' : 'Ver pagamentos'}>
                                                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>{dashIfEmpty(liquidation.liquidationNumber)}</TableCell>
                                        <TableCell>{dashIfEmpty(formatDate(liquidation.liquidationDate))}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(liquidation.liquidatedAmount)}</TableCell>
                                        <TableCell>{dashIfEmpty(liquidation.observations)}</TableCell>
                                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                            <Box sx={{ display: 'inline-flex' }}>
                                                <RowAction title="Editar" onClick={() => ctrl.openDrawer('liquidationDrawer', { record: liquidation })}><EditIcon fontSize="small" /></RowAction>
                                                <RowAction title="Excluir" color="error" onClick={() => ctrl.deleteLiquidation(liquidation)}><DeleteIcon fontSize="small" /></RowAction>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    {isExpanded && (
                                        <TableRow>
                                            <TableCell colSpan={COLUMN_COUNT} sx={{ p: 0, border: 'none' }}>
                                                <PaymentsPanel liquidation={liquidation} payments={payments} ctrl={ctrl} />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </Paper>
        </Box>
    );
}
